import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Trigger, TriggerAction} from "types/Trigger";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import Chip from "components/Chip";
import DeleteTrigger from "pages/Trigger/Delete";
import {toTitleCase} from 'services/string';
import {
  CheckCircleIcon,
  ChevronDoubleDownIcon,
  CodeIcon,
  ExclamationCircleIcon,
  XCircleIcon
} from "@heroicons/react/outline";
import TriggerType from "types/TriggerType";
import Empty from "components/Empty";
import TriggerActionType from "types/TriggerActionType";
import StackedListItem from "components/StackedListItem";
import StackedList from 'components/StackedList';
import BooleanIndicator from "components/BooleanIndicator";
import {TimeConstraint} from "types/TriggerTimings";
import {useOperator} from "../../../context/OperatorContext";

// Describe what an action does
function describeAction(action: TriggerAction, termFor: (term: string) => string): string {
  switch (action.action_type) {
    case TriggerActionType.EXHIBIT:
      return `Displays ${termFor("exhibit")} '${action.exhibit.name}' for the audience member`

    case TriggerActionType.FACILITY:
      return `Displays ${termFor("facility")} '${action.facility.name}' for the audience member`

    case TriggerActionType.MESSAGE:
      return `Sends message '${action.message.subject}' to the audience member`

    case TriggerActionType.URL:
      return `Navigates to '${action.url}'`

    case TriggerActionType.OFFER:
      return `Grants offer '${action.offer.name}' to the audience member`

    default:
      return "Unknown action behaviour"
  }
}

// Check if a trigger has specific timings
function hasTimings(trigger: Trigger): boolean {
  return triggerTimes(trigger)
    .some(([, constraint]) => constraint.frequency !== 'always');
}

function triggerTimes(trigger: Trigger) {
  return Object
    .entries(trigger.timings)
    .filter(([key]) => key !== 'timezone') as Array<[string, TimeConstraint]>
}

function ViewTrigger() {
  const {termFor} = useOperator();

  const {id} = useParams();
  const {data: trigger, isLoading} = useQuery(
    ['trigger', {id}],
    () => ngage.triggers.get(Number(id))
  )

  const [deletingTrigger, setDeletingTrigger] = useState<Trigger | null>(null);
  const navigate = useNavigate()

  // Prepare action handlers
  function handleDeleteTrigger() {
    if (trigger) setDeletingTrigger(trigger)
  }

  function handleEditTrigger() {
    if (id) navigate(`/triggers/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteTrigger}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditTrigger}>Edit</Button>
  </>

  return (
    <Page
      title={trigger?.name || "View Trigger"}
      breadcrumbs={{"Triggers": "/triggers", [trigger?.name || "View Trigger"]: `/triggers/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {!!trigger &&
        <>
          {/* Trigger Information */}
          <section>
            <Card
              heading="Trigger Information"
              subheading={`${toTitleCase(trigger.trigger_type)} trigger`}
            >
              <DescriptionList>
                <DescriptionListItem span={2}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    {trigger.is_enabled ?
                      <><CheckCircleIcon className="text-green-600 h-5 w-5 inline align-bottom"/> Enabled</> :
                      <><XCircleIcon className="text-red-600 h-5 w-5 inline align-bottom"/> Disabled</>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Target Audience Segment</DescriptionTerm>
                  <DescriptionDetails>
                    {trigger.segment ?
                      <Chip
                        name={trigger.segment.name}
                        description={`${trigger.segment.audience_size} people`}
                        href={`/audience-segments/${trigger.segment.id}`}
                      /> :
                      'All audience members'
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Timings</DescriptionTerm>
                  <DescriptionDetails>
                    {hasTimings(trigger) ?
                      <StackedList>
                        {triggerTimes(trigger).map(([day, constraint]) => (
                          <StackedListItem key={day}>
                            <div className="flex justify-between w-full">
                              <div className="font-semibold">{toTitleCase(day)}</div>
                              <div>
                                {constraint.frequency === 'between' ?
                                  <div>
                                    <ExclamationCircleIcon className="h-5 w-5 text-yellow-600 inline align-bottom"/>
                                    <span className="ml-1">
                                      Executes between
                                      {' '}
                                      {constraint.periods[0][0]}
                                      {' '}
                                      and
                                      {' '}
                                      {constraint.periods[0][1]}
                                    </span>
                                  </div> :
                                  <div>
                                    <BooleanIndicator value={constraint.frequency === 'always'}/>
                                    <span className="ml-1">{toTitleCase(constraint.frequency)} executes</span>
                                  </div>
                                }
                              </div>
                            </div>
                          </StackedListItem>
                        ))}
                      </StackedList> :
                      <span>Always executes</span>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                {trigger.trigger_type === TriggerType.ACTIVATION &&
                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Hardware ({trigger.hardware_count})</DescriptionTerm>
                    <DescriptionDetails>
                      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {trigger.hardware.map((hardware, index) => (
                          <li key={index}>
                            <Chip
                              name={hardware.name}
                              description={toTitleCase(hardware.type)}
                              href={`/${hardware.type}s/${hardware.id}`}
                            />
                          </li>
                        ))}
                      </ul>
                    </DescriptionDetails>
                  </DescriptionListItem>
                }
              </DescriptionList>
            </Card>
          </section>

          {/* Actions */}
          <section className="mt-4">
            <Card heading="Actions">
              {!!trigger.actions?.length ? (
                <ul className="grid gap-4">
                  {trigger.actions.map((action, index) => (
                    <li key={index} className="flex flex-col items-center">
                      <div className="w-full">
                        <Chip
                          name={action.name}
                          description={describeAction(action, termFor)}
                          href={`/triggers/${trigger.id}/edit`}
                        />
                      </div>
                      {index < ((trigger.actions?.length || 0) - 1) &&
                        <ChevronDoubleDownIcon className="h-5 w-5 text-gray-700 mt-3"/>
                      }
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty
                  icon={CodeIcon}
                  text="No actions"
                  hint="Define actions for this trigger to see them here."
                />
              )}
            </Card>
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteTrigger
        show={trigger === deletingTrigger}
        trigger={deletingTrigger}
        onCancel={() => setDeletingTrigger(null)}
        onDelete={() => navigate('/triggers')}
        onDeleteFail={() => setDeletingTrigger(null)}
      />
    </Page>
  );
}

export default ViewTrigger;